/* W7.5B — Pipeline 5 phases : Deals MAÎTRES uniquement (pas de registre
 * concurrent). Transition = même endpoint POST /deals/{id}/phase que Deal 360,
 * AUCUNE logique métier parallèle côté frontend. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Loader2, ChevronLeft, ChevronRight, AlertTriangle, Briefcase, FileText, Scale } from "lucide-react";

const fmtCHF = (v) =>
  new Intl.NumberFormat("fr-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 0 }).format(v || 0);
const PHASE_LABELS = { discover: "Discover", qualify: "Qualify", structure: "Structure", execute: "Execute", close: "Close" };
const PHASE_COLORS = { discover: "#378ADD", qualify: "#7F77DD", structure: "#EFD127", execute: "#EF9F27", close: "#1D9E75" };
const scoreColor = (s) => (s >= 70 ? "#1D9E75" : s >= 45 ? "#EF9F27" : "#E24B4A");

function DealCard({ card, phases, onTransition, busy }) {
  const idx = phases.indexOf(card.phase);
  return (
    <div className="noam-card p-3 space-y-2" data-testid={`pipeline-card-${card.id}`}>
      <Link to={`/app/deals/${card.id}`} className="block group">
        <div className="text-xs font-medium text-zinc-100 group-hover:text-white line-clamp-2">{card.title}</div>
        <div className="text-[10px] text-zinc-500 flex items-center gap-1.5 mt-1 flex-wrap">
          <span className="text-[#378ADD]">{card.asset_label}</span>
          <span>·</span><span>{card.valeur_chf ? fmtCHF(card.valeur_chf) : "valeur non renseignée"}</span>
          {card.source_type === "courtage" && <Briefcase className="w-2.5 h-2.5 text-[#EFD127]" />}
        </div>
      </Link>
      <div className="flex items-center gap-2 text-[10px] text-zinc-500">
        {card.score_global != null && (
          <span className="font-mono font-bold" style={{ color: scoreColor(card.score_global) }}>{card.score_global}</span>
        )}
        {card.doc_count > 0 && <span className="flex items-center gap-0.5"><FileText className="w-2.5 h-2.5" />{card.doc_count}</span>}
        {card.nego_count > 0 && <span className="flex items-center gap-0.5"><Scale className="w-2.5 h-2.5" />{card.nego_count}</span>}
        {card.attention && <AlertTriangle className="w-3 h-3 text-[#EF9F27]" />}
        {card.terminal && <span className="px-1.5 py-0.5 rounded-full border border-white/10">{card.statut}</span>}
      </div>
      {!card.terminal && (
        <div className="flex items-center justify-between pt-1 border-t border-white/5">
          <button disabled={idx <= 0 || busy} onClick={() => onTransition(card, phases[idx - 1])}
            className="btn-ghost text-[10px] py-0.5 px-1.5 disabled:opacity-20"
            title={idx > 0 ? `Revenir à ${PHASE_LABELS[phases[idx - 1]]}` : ""}
            data-testid={`pipeline-back-${card.id}`}>
            <ChevronLeft className="w-3 h-3" />
          </button>
          <button disabled={idx >= phases.length - 1 || busy} onClick={() => onTransition(card, phases[idx + 1])}
            className="btn-ghost text-[10px] py-0.5 px-1.5 disabled:opacity-20 text-[#EFD127]"
            title={idx < phases.length - 1 ? `Avancer vers ${PHASE_LABELS[phases[idx + 1]]}` : ""}
            data-testid={`pipeline-advance-${card.id}`}>
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function DealPipeline() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/workspace/deals/cockpit")
      .then((r) => setData(r.data))
      .catch((e) => notify.error(fmtError(e)));
  useEffect(() => { load(); }, []);

  const transition = async (card, to) => {
    setBusy(true);
    try {
      const reason = window.prompt(`Raison du passage vers ${PHASE_LABELS[to]} (optionnel) :`) || undefined;
      const r = await api.post(`/workspace/deals/${card.id}/phase`, { to, reason });
      if (r.data.already) notify.info("Phase déjà à jour.");
      else notify.success(`« ${card.title} » : ${PHASE_LABELS[r.data.from]} → ${PHASE_LABELS[to]}`);
      await load();
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };

  if (!data) return <div className="flex items-center justify-center py-20 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="deal-pipeline-root">
      <div>
        <div className="noam-overline">Deal System</div>
        <h1 className="noam-display text-2xl font-bold tracking-tight">Pipeline</h1>
        <p className="text-xs text-zinc-500 mt-1">
          Discover → Qualify → Structure → Execute → Close. Les cartes sont les Deals maîtres — un clic ouvre la fiche 360.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {data.phases.map((p) => (
          <div key={p} id={p} className="space-y-2" data-testid={`deal-phase-col-${p}`}>
            <div className="flex items-center gap-2 px-1">
              <span className="w-2 h-2 rounded-full" style={{ background: PHASE_COLORS[p] }} />
              <span className="text-[11px] font-medium text-zinc-300">{PHASE_LABELS[p]}</span>
              <span className="text-[10px] text-zinc-600 font-mono">{data.distribution[p] || 0}</span>
            </div>
            <div className="space-y-2 min-h-[80px]">
              {(data.pipeline[p] || []).map((c) => (
                <DealCard key={c.id} card={c} phases={data.phases} onTransition={transition} busy={busy} />
              ))}
              {(data.pipeline[p] || []).length === 0 && (
                <div className="border border-dashed border-white/8 rounded-lg p-4 text-center text-[10px] text-zinc-700"
                  data-testid={`pipeline-empty-${p}`}>
                  Aucun deal
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
